import { MediaPlayerInstance, useMediaState } from "@vidstack/react";
import { _ } from "i18n";
import type { MouseEvent, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Loader from "ui-kit/src/Loader/Loader";
import Modal from "ui-kit/src/Modal";
import { UserDownloadActivityTags } from "../../../../assets/js/types";
import FavoriteButtonBig from "../../../ui-kit/src/FavoriteButton/FavoriteButtonBig";
import { VideoPlayerContextProvider } from "./Contexts/contexts";
import { FullscreenModalPortalProvider } from "./FullscreenModalPortalProvider";
import { GameAdminOptions } from "./components/Admin/GameAdminOptions";
import { InsightPage } from "./components/DataInsights";
import { DataInsightProvider } from "./components/DataInsights/contexts/DataInsightContext";
import { GamePageHeader, type GamePageHeaderBackLink } from "./components/GamePageHeader";
import { MainNavBar } from "./components/MainNavBar";
import { GeneratedReport } from "./components/PhysicalAnalysis";
import { PlayerStats } from "./components/PlayerStats/PlayerStats";
import { StatsLinedTabsAdapter } from "./components/StatsLinedTabsAdapter";
import { TeamStats } from "./components/TeamStats/TeamStats";
import { Clips } from "./components/Video/Clips/Clips";
import { EventListArrowHandler } from "./components/Video/EventList/EventListArrowHandler";
import { GameVideo } from "./components/Video/GameVideo";
import { TeamSheetModal } from "./components/Video/TeamSheet/TeamSheetModal";
import "./gamePage.css";
import type { ReportEventErrorHandler } from "./report-error";
import { useGameStore } from "./store";
import {
	BadgesPutRequest,
	CoachReviewPromises,
	GameEventCommentProps,
	GamePlayerStatResponse,
	NavOptions,
	PhysicalAnalysis,
	PlayerGradingSubmitPayload,
	RatingPutRequest,
	saveUserWidgetSettingsRequestBody,
	type ClipsUtilityProps,
	type Colors,
	type GPGameTeamResponse,
	type GameEventDataExportRequest,
	type GameEventFilterUtilityProps,
	type GameInsightsSettingsProps,
	type GamePageLazyResponse,
	type GamePageResponse,
	type GamePlayerItemRequest,
	type GameStatsExportRequest,
	type GameTeamPutColorsRequest,
	type GameTeamSheetCompositionPlayersResponse,
	type GameTeamSheetGameTeamPlayersSearchResponse,
	type GameTeamSheetPlayersResponse,
	type HighlightHandlers,
} from "./types";
import { useFullscreenModalContainer } from "./useFullscreenModalContainer";

export interface GamePageContainerProps {
	toastMessage: (type: string, message: string) => void;
	gameData: GamePageResponse;
	gameDataLazy?: GamePageLazyResponse;
	locale: string;
	clips: ClipsUtilityProps;
	gameEventFilters?: GameEventFilterUtilityProps;
	currentNavOption: NavOptions;
	handleChangeColors: (id: number, colors: Colors) => void;
	handleChangeScore: (id: number, scores: { homeTeam?: number; awayTeam?: number }) => void;
	handleChangeNavOptions: (tab: NavOptions) => void;
	handleGameStatsExport: (tableExportRequest: GameStatsExportRequest) => void;
	handleGameEventDataExport: (gameEventDataExportRequest: GameEventDataExportRequest) => void;
	highlightsHandlers: HighlightHandlers;
	onFavoriteClick?: (e: MouseEvent<HTMLDivElement>) => void;
	favoriteDisabled?: boolean;
	isFavorite?: boolean | null;
	handleTeamSheetEdit: (
		id: number,
		squad: GamePlayerItemRequest[],
		substitutes: GamePlayerItemRequest[],
		colors: GameTeamPutColorsRequest,
		saveAnyway: boolean,
		gender?: string | null
	) => Promise<boolean>;
	loadCompositionPlayers: (teamId: number, composition: string) => Promise<GameTeamSheetCompositionPlayersResponse | null>;
	searchPlayers: (teamId: number, filter: string) => Promise<GameTeamSheetGameTeamPlayersSearchResponse | null>;
	loadClubPlayers: (teamId: number) => Promise<GameTeamSheetPlayersResponse | null>;
	handleLeftSideStart?: (homeTeamStartsOnLeft: boolean | null) => void;
	handleChangeDescription: (id: number, description: string | null) => void;
	handleChangeAnalysisApproval?: (approvalStatus: "approved" | "disapproved") => Promise<boolean>;
	handleAbortGameResync?: () => void;
	handleGameResync?: (resyncType: string | null, syncLocally: boolean | null, resyncAfter: string | null, skipReview: boolean) => void;
	canSwapHomeAwayMapping?: boolean;
	isSwappingHomeAwayMapping?: boolean;
	handleSwapHomeAwayMapping?: () => void;
	swapped?: boolean | null;
	keepMappingAllowed?: boolean;
	domainURL: string;
	isAdmin?: boolean;
	showShareButton?: boolean;
	handleTeamSheetDataExport: (type: string) => void;
	handleSetPlayerRating?: (id: number, rating: RatingPutRequest) => void;
	handleSetPlayerBadges?: (id: number, badges: BadgesPutRequest) => void;
	handleSubmitPlayerGrading?: (payload: PlayerGradingSubmitPayload) => void | Promise<void>;
	gameEventComment?: GameEventCommentProps;
	handleReportError?: ReportEventErrorHandler;
	gameInsightsSettings: GameInsightsSettingsProps;
	showCoachToolbar: boolean;
	storeDownloadVideoActivity: (tag: UserDownloadActivityTags, gameId: number) => void;
	physicalAnalysisReport?: PhysicalAnalysis;
	renderAIGameSummary?: () => JSX.Element;
	gamePlayerStat?: GamePlayerStatResponse | null;
	gamePlayerStatIsLoading?: boolean;
	extraAdminOptions?: ReactNode;
	coachReviewPromises?: CoachReviewPromises;
	userId?: number;
	activeContextTeamId?: number;
	backLink?: GamePageHeaderBackLink;
	editGameHref?: string;
	editGameLabel?: string;
	allowEditDescription?: boolean;
	showLocation?: boolean;
}

export function GamePageContainer({
	toastMessage,
	isAdmin,
	showShareButton,
	domainURL,
	gameData,
	gameDataLazy,
	locale,
	currentNavOption,
	favoriteDisabled,
	clips,
	gameEventFilters,
	isFavorite,
	handleChangeColors,
	handleChangeScore,
	onFavoriteClick,
	handleChangeNavOptions,
	handleTeamSheetEdit,
	handleGameStatsExport,
	handleGameEventDataExport,
	loadCompositionPlayers,
	searchPlayers,
	loadClubPlayers,
	highlightsHandlers,
	handleLeftSideStart,
	handleChangeDescription,
	handleChangeAnalysisApproval,
	handleAbortGameResync,
	handleGameResync,
	canSwapHomeAwayMapping,
	isSwappingHomeAwayMapping,
	handleSwapHomeAwayMapping,
	swapped,
	keepMappingAllowed,
	handleTeamSheetDataExport,
	handleSetPlayerRating,
	handleSetPlayerBadges,
	handleSubmitPlayerGrading,
	gameEventComment,
	handleReportError,
	gameInsightsSettings,
	showCoachToolbar,
	storeDownloadVideoActivity,
	physicalAnalysisReport,
	renderAIGameSummary,
	gamePlayerStat,
	gamePlayerStatIsLoading,
	extraAdminOptions,
	coachReviewPromises,
	userId,
	activeContextTeamId,
	backLink,
	editGameHref,
	editGameLabel,
	allowEditDescription,
	showLocation,
}: GamePageContainerProps) {
	const playerRef = useRef<MediaPlayerInstance>(null);
	const canPlay = useMediaState("canPlay", playerRef);
	const { eventFilter } = useGameStore();

	useEffect(() => {
		if (currentNavOption !== NavOptions.FullGame) {
			//@ts-ignore
			playerRef.current = null; // There's no player outside the FullGame tab
		}
	}, [currentNavOption]);

	const dataTabs = useMemo(
		() => [
			...(gameData.permissions.showTeamStats
				? [
						{
							key: NavOptions.TeamStats,
							label: _("game.tab.team_stats"),
						},
					]
				: []),
			...(gameData.permissions.showPlayerStats
				? [
						{
							key: NavOptions.PlayerStats,
							label: _("game.tab.player_stats"),
						},
					]
				: []),
		],
		[gameData.permissions.showTeamStats, gameData.permissions.showPlayerStats]
	);

	const videoTabs = useMemo(
		() => [
			{
				key: NavOptions.FullGame,
				label: _("game.tab.full_game"),
			},
			...(gameData.permissions.useClips && clips.data?.items?.length
				? [
						{
							key: NavOptions.Clips,
							label: _("game.tab.clips"),
						},
					]
				: []),
		],
		[gameData.permissions.useClips, clips.data?.items?.length]
	);

	const [showTeamSheetEditModal, setShowTeamSheetEditModal] = useState(false);
	const [selectedTeam, setSelectedTeam] = useState<GPGameTeamResponse | null>(null);
	const [showForceSave, setShowForceSave] = useState(false);
	const [isTeamSheetSaving, setIsTeamSheetSaving] = useState(false);
	const fullscreenModalContainer = useFullscreenModalContainer();

	const handleOpenEditTeamSheetModal = (team: GPGameTeamResponse) => {
		setSelectedTeam(team);
		setShowTeamSheetEditModal(true);
	};

	const handleCloseEditTeamSheetModal = () => {
		setShowTeamSheetEditModal(false);
		setSelectedTeam(null);
	};

	const editTeamSheet = async (
		id: number,
		squad: GamePlayerItemRequest[],
		substitutes: GamePlayerItemRequest[],
		colors: GameTeamPutColorsRequest,
		saveAnyway: boolean,
		gender?: string | null
	) => {
		if (isTeamSheetSaving) {
			return;
		}
		setIsTeamSheetSaving(true);

		const success = await handleTeamSheetEdit(id, squad, substitutes, colors, saveAnyway, gender);

		if (success) {
			setShowTeamSheetEditModal(false);
			setShowForceSave(false);
		} else {
			setShowForceSave(true);
		}
		setIsTeamSheetSaving(false);
	};

	useEffect(() => {
		if (currentNavOption === NavOptions.Clips && !clips.isLoading && !clips.data?.items?.length) {
			handleChangeNavOptions(NavOptions.FullGame);
		}
	}, [clips.data?.items, clips.isLoading, currentNavOption, handleChangeNavOptions]);

	const teamInfo = useMemo(
		() => ({
			homeTeam: {
				id: gameData.homeGameTeam.id,
				shirtColor: gameData.homeGameTeam.colorShirt,
				clubLogo: gameData.homeGameTeam.logo,
				clubName: gameData.homeGameTeam.clubName ?? gameData.homeGameTeam.teamName,
			},
			awayTeam: {
				id: gameData.awayGameTeam.id,
				shirtColor: gameData.awayGameTeam.colorShirt,
				clubLogo: gameData.awayGameTeam.logo,
				clubName: gameData.awayGameTeam.clubName ?? gameData.awayGameTeam.teamName,
			},
		}),
		[
			gameData.homeGameTeam.id,
			gameData.homeGameTeam.colorShirt,
			gameData.homeGameTeam.logo,
			gameData.homeGameTeam.clubName,
			gameData.homeGameTeam.teamName,
			gameData.awayGameTeam.id,
			gameData.awayGameTeam.colorShirt,
			gameData.awayGameTeam.logo,
			gameData.awayGameTeam.clubName,
			gameData.awayGameTeam.teamName,
		]
	);

	const teamPlayers = useMemo(() => {
		const awayTeamPlayers = gameData.awayGameTeam.players;
		const homeTeamPlayers = gameData.homeGameTeam.players;
		return gameData.awayGameTeam.permissions.editTeamSheet ? awayTeamPlayers : homeTeamPlayers;
	}, [gameData.awayGameTeam.permissions.editTeamSheet, gameData.awayGameTeam.players, gameData.homeGameTeam.players]);

	const ratingAndBadges = useMemo(() => {
		const awayTeamPermissions = gameData.awayGameTeam.permissions.ratingBadgeEdit;
		const homeTeamPermissions = gameData.homeGameTeam.permissions.ratingBadgeEdit;

		return gameData.awayGameTeam.permissions.editTeamSheet ? awayTeamPermissions : homeTeamPermissions;
	}, [
		gameData.awayGameTeam.permissions.editTeamSheet,
		gameData.awayGameTeam.permissions.ratingBadgeEdit,
		gameData.homeGameTeam.permissions.ratingBadgeEdit,
	]);

	// Memoize players array to prevent recreation on every render
	const players = useMemo(
		() => [...gameData.homeGameTeam.players, ...gameData.awayGameTeam.players],
		[gameData.homeGameTeam.players, gameData.awayGameTeam.players]
	);

	// Memoize handleSaveWidgetSettings callback
	const handleSaveWidgetSettings = useCallback(
		async (settings: saveUserWidgetSettingsRequestBody) => {
			if (gameInsightsSettings?.saveSettings) {
				await gameInsightsSettings.saveSettings(settings);
			}
		},
		[gameInsightsSettings]
	);

	return (
		<div className="gamePage-container">
			<GamePageHeader
				permissions={gameData?.permissions}
				locale={locale}
				gameData={gameData?.game}
				homeTeam={gameData?.homeGameTeam}
				awayTeam={gameData?.awayGameTeam}
				handleChangeDesc={handleChangeDescription}
				handleChangeColors={handleChangeColors}
				handleChangeScore={handleChangeScore}
				backLink={backLink}
				editGameHref={editGameHref}
				editGameLabel={editGameLabel}
				allowEditDescription={allowEditDescription}
				showLocation={showLocation}
			/>
			<VideoPlayerContextProvider playerRef={playerRef} canPlay={canPlay}>
				{renderAIGameSummary && <div className="aiGameSummary-container">{renderAIGameSummary()}</div>}
				<div className="navbarAndFavorite-container">
					{gameData.permissions?.saveFavorites && onFavoriteClick && (
						<div className="navBarFavoriteButton">
							<FavoriteButtonBig favoriteDisabled={favoriteDisabled} isFavorite={isFavorite} handleFavorite={onFavoriteClick} />
						</div>
					)}
					<MainNavBar
						gameData={gameData}
						gameDataLazy={gameDataLazy}
						permissions={gameData.permissions}
						selectedTab={currentNavOption}
						handleSelectTab={handleChangeNavOptions}
					/>
				</div>
				<FullscreenModalPortalProvider>
					<EventListArrowHandler />

					{currentNavOption === NavOptions.FullGame && (
						<>
							<StatsLinedTabsAdapter tabs={videoTabs} active={currentNavOption} onChange={handleChangeNavOptions} />
							<GameVideo
								gameData={gameData}
								gameDataLazy={gameDataLazy}
								highlightsHandlers={highlightsHandlers}
								handleCreateClip={clips.create}
								gameEventFilters={gameEventFilters}
								handleOpenEditTeamSheetModal={handleOpenEditTeamSheetModal}
								handleGameEventDataExport={handleGameEventDataExport}
								handleTeamSheetDataExport={handleTeamSheetDataExport}
								handleSetPlayerRating={handleSetPlayerRating}
								handleSetPlayerBadges={handleSetPlayerBadges}
								handleSubmitPlayerGrading={handleSubmitPlayerGrading}
								gameEventComment={gameEventComment}
								players={players}
								onReportError={handleReportError}
								handleChangeNavOptions={handleChangeNavOptions}
								showCoachToolbar={showCoachToolbar}
								teamPlayers={teamPlayers}
								ratingAndBadges={ratingAndBadges}
								storeDownloadVideoActivity={storeDownloadVideoActivity}
								toastMessage={toastMessage}
								coachReviewPromises={coachReviewPromises}
								userId={userId}
								activeContextTeamId={activeContextTeamId}
							/>
						</>
					)}
					{currentNavOption === NavOptions.Clips && (
						<>
							<StatsLinedTabsAdapter tabs={videoTabs} active={currentNavOption} onChange={handleChangeNavOptions} />
							<Clips
								toastMessage={toastMessage}
								isAdmin={isAdmin}
								showShareButton={showShareButton}
								domainURL={domainURL}
								teamInfo={teamInfo}
								isClipsLoading={clips.isLoading}
								isSelectedClipLoading={clips.isSelectedClipLoading}
								selectedClip={clips.selectedClip}
								clips={clips}
							/>
						</>
					)}
					{currentNavOption === NavOptions.TeamStats && (
						<TeamStats
							gameData={gameData}
							gameDataLazy={gameDataLazy}
							tabs={dataTabs}
							currentNavOption={currentNavOption}
							handleChangeNavOptions={handleChangeNavOptions}
							handleGameStatsExport={handleGameStatsExport}
						/>
					)}
					{currentNavOption === NavOptions.PlayerStats && (
						<PlayerStats
							gameData={gameData}
							gameDataLazy={gameDataLazy}
							gamePlayerStat={gamePlayerStat}
							gamePlayerStatIsLoading={gamePlayerStatIsLoading}
							tabs={dataTabs}
							currentNavOption={currentNavOption}
							handleChangeNavOptions={handleChangeNavOptions}
							handleGameStatsExport={handleGameStatsExport}
						/>
					)}
					{currentNavOption === NavOptions.Admin && (
						<GameAdminOptions
							toastMessage={toastMessage}
							syncRunning={gameData.game.analysisSyncRunning}
							leftSideStart={gameData.game.leftSideStart}
							handleLeftSideStart={handleLeftSideStart}
							handleApprove={handleChangeAnalysisApproval}
							handleAbortResync={handleAbortGameResync}
							handleResync={handleGameResync}
							analysisApproved={gameData.game.isAnalysisApproved}
							physicalAnalysisReportData={physicalAnalysisReport?.data}
							handlePostPhysicalAnalysisReportUrls={physicalAnalysisReport?.handlePostPhysicalAnalysisReportUrls}
							extraAdminOptions={extraAdminOptions}
							canSwapHomeAwayMapping={canSwapHomeAwayMapping}
							isSwappingHomeAwayMapping={isSwappingHomeAwayMapping}
							handleSwapHomeAwayMapping={handleSwapHomeAwayMapping}
							swapped={swapped}
							keepMappingAllowed={keepMappingAllowed}
						/>
					)}

					{showTeamSheetEditModal && selectedTeam && (
						<Modal
							show={showTeamSheetEditModal}
							size="lg"
							title={_("matchSheet.title")}
							onClose={handleCloseEditTeamSheetModal}
							UNSTABLE_portalContainer={fullscreenModalContainer}
						>
							<TeamSheetModal
								canEditColors={gameData.awayGameTeam.permissions.editColors || gameData.homeGameTeam.permissions.editColors}
								selectedTeam={selectedTeam}
								handleTeamSheetEdit={editTeamSheet}
								loadCompositionPlayers={loadCompositionPlayers}
								searchPlayers={searchPlayers}
								isLockedForCurrentUser={gameData.game.isLockedForActualUser}
								loadClubPlayers={loadClubPlayers}
								positions={gameData.playerPositions}
								showForceSave={showForceSave}
								canRemovePlayers={gameData.awayGameTeam.permissions.removePlayers || gameData.homeGameTeam.permissions.removePlayers}
								canEditNumbers={gameData.awayGameTeam.permissions.changeNumbers || gameData.homeGameTeam.permissions.changeNumbers}
								canAnonymizePlayers={
									gameData.awayGameTeam.permissions.anonymizePlayers || gameData.homeGameTeam.permissions.anonymizePlayers
								}
								canEditGender={
									selectedTeam.id === gameData.homeGameTeam.id
										? gameData.homeGameTeam.permissions.editGender
										: gameData.awayGameTeam.permissions.editGender
								}
							/>
						</Modal>
					)}

					<div style={{ display: currentNavOption === NavOptions.DataInsights ? "block" : "none" }}>
						<DataInsightProvider handleChangeNavOptions={handleChangeNavOptions}>
							{gameInsightsSettings?.gamePageIsLoading || !eventFilter ? (
								<Loader />
							) : (
								<InsightPage
									gameData={gameData}
									handleSaveWidgetSettings={handleSaveWidgetSettings}
									events={gameDataLazy?.events}
									toastMessage={toastMessage}
									userWidgetSettingsData={gameInsightsSettings?.userWidgetSettingsData}
								/>
							)}
						</DataInsightProvider>
					</div>

					{currentNavOption === NavOptions.PhysicalAnalysis && gameData.permissions.showPhysicalAnalysis && (
						<GeneratedReport
							physicalAnalysisReport={physicalAnalysisReport?.data}
							isLoading={physicalAnalysisReport?.isLoading}
							error={physicalAnalysisReport?.error}
						/>
					)}
				</FullscreenModalPortalProvider>
			</VideoPlayerContextProvider>
		</div>
	);
}
